import { useState, useEffect, useCallback } from 'react';
import Navigation from '../tulote/Navigation';
import Footer from '../tulote/Footer';
import ThemeTogglerTwo from '../tulote/ThemeTogglerTwo';
import { ThemeProvider } from '../ThemeProvider';
import brandConfig from '../../config/brandConfig';
import {
  Smartphone,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Globe,
  Users,
  Layers,
  MessageCircle,
  Eye,
  CheckCircle,
  Download,
} from 'lucide-react';

const screenshots = [
  { src: '/mobile-app/1.jpeg', alt: 'Pantalla principal - Encuentra tu lote ideal', caption: 'Selecciona tu país y encuentra lotes disponibles' },
  { src: '/mobile-app/2.jpeg', alt: 'Mapa interactivo con detalle de lotes', caption: 'Visualiza lotes en el mapa con información detallada' },
  { src: '/mobile-app/3.jpeg', alt: 'Vista satelital del proyecto', caption: 'Explora proyectos con vista satelital y etapas' },
  { src: '/mobile-app/4.jpeg', alt: 'Contacto directo por WhatsApp', caption: 'Contacta al asesor directamente por WhatsApp' },
  { src: '/mobile-app/5.jpeg', alt: 'Vista de mapa del proyecto', caption: 'Navega por el proyecto en vista de mapa' },
  { src: '/mobile-app/6.jpeg', alt: 'Detalle del lote con agentes', caption: 'Consulta detalles del lote y agentes disponibles' },
];

const features = [
  {
    icon: MapPin,
    title: 'Mapa interactivo',
    description: 'Explora lotes geolocalizados en un mapa interactivo con zoom y detalle.',
  },
  {
    icon: Globe,
    title: 'Multipaís',
    description: 'Disponible en Perú, México, Colombia, Chile y Ecuador.',
  },
  {
    icon: Eye,
    title: 'Vista satelital',
    description: 'Visualiza los proyectos con vista satelital y etapas del desarrollo.',
  },
  {
    icon: Layers,
    title: 'Disponibilidad en tiempo real',
    description: 'Consulta qué lotes están disponibles, reservados o vendidos.',
  },
  {
    icon: MessageCircle,
    title: 'Contacto directo',
    description: 'Comunícate con un asesor al instante por WhatsApp.',
  },
  {
    icon: Users,
    title: 'Agentes inmobiliarios',
    description: 'Visualiza los agentes asignados y contacta al que prefieras.',
  },
];

function MobileAppPage() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = useCallback(() => setCurrentIndex((i) => (i === screenshots.length - 1 ? 0 : i + 1)), []);
  const prev = () => setCurrentIndex((i) => (i === 0 ? screenshots.length - 1 : i - 1));

  // Auto-scroll every 3s
  useEffect(() => {
    const timer = setInterval(next, 3000);
    return () => clearInterval(timer);
  }, [next]);

  // Load TikTok embed script
  useEffect(() => {
    if (!document.getElementById('tiktok-embed-script')) {
      const script = document.createElement('script');
      script.id = 'tiktok-embed-script';
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.lotespro.app';

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
        <Navigation />

        {/* Hero */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Info */}
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-full mb-4">
                  <Smartphone className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-bold text-green-700 dark:text-green-300">
                    App Móvil
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                  {brandConfig.name} en tu celular
                </h1>

                <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
                  Explora lotes disponibles desde cualquier lugar. Visualiza proyectos en el mapa,
                  consulta disponibilidad en tiempo real y contacta a un asesor al instante.
                </p>

                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg mb-6">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                    Disponible en Google Play
                  </span>
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Descarga la app desde Google Play y explora lotes disponibles en Perú, México, Colombia, Chile y Ecuador.
                  Encuentra tu lote ideal directamente desde tu celular.
                </p>

                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Descargar en Google Play
                </a>
              </div>

              {/* Right - Phone mockup */}
              <div className="flex flex-col items-center">
                <div className="relative w-64 sm:w-72">
                  <div className="relative bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10"></div>
                    <div className="rounded-[2rem] overflow-hidden bg-white aspect-[9/19.5]">
                      <img
                        src={screenshots[currentIndex].src}
                        alt={screenshots[currentIndex].alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center min-h-[2.5rem]">
                  {screenshots[currentIndex].caption}
                </p>

                {/* Navigation */}
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={prev}
                    className="w-10 h-10 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <div className="flex gap-2">
                    {screenshots.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${
                          i === currentIndex
                            ? 'bg-green-600 dark:bg-green-400'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={next}
                    className="w-10 h-10 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Funcionalidades del app
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Todo lo que necesitas para explorar y encontrar tu lote ideal, desde tu celular.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className="p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tutorial */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Tutorial de {brandConfig.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-xl mx-auto">
              Mira cómo funciona la app en este video tutorial.
            </p>
            <div className="flex justify-center">
              <blockquote
                className="tiktok-embed"
                cite="https://www.tiktok.com/@lotespro/video/7609339340874910993"
                data-video-id="7609339340874910993"
                style={{ maxWidth: 605, minWidth: 325 }}
              >
                <section>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    title="@lotespro"
                    href="https://www.tiktok.com/@lotespro?refer=embed"
                  >
                    @lotespro
                  </a>
                </section>
              </blockquote>
            </div>
          </div>
        </section>

        {/* Download CTA */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border border-green-200 dark:border-green-800">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-full mb-4">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                  Disponible en Google Play
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Descarga la app ahora
              </h2>

              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Encuentra tu lote ideal desde tu celular. Explora proyectos en el mapa,
                consulta disponibilidad en tiempo real y contacta a un asesor al instante.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  100% gratuita
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  Mapa interactivo
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  Contacto directo con asesores
                </div>
              </div>

              <a
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-lg"
              >
                <Download className="w-5 h-5" />
                Descargar en Google Play
              </a>
            </div>
          </div>
        </section>

        <Footer />

        <div className="fixed bottom-6 right-6 z-50 hidden sm:block">
          <ThemeTogglerTwo />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default MobileAppPage;
